class QuickViewDrawer extends MenuDrawer {
  constructor() {
    super();

    this.setClasses({
      open: 'quick-view--open',
      opening: 'quick-view--opening',
      closing: 'quick-view--closing'
    });

    this.addEventListener('close', () => {
      const drawerContent = this.querySelector('.quick-view__content');
      drawerContent.innerHTML = '';
      drawerContent.classList.remove('hide-cover');

      document.dispatchEvent(new CustomEvent('quickview:close'));
    });
  }
}
customElements.define('quick-view-drawer', QuickViewDrawer);

class QuickViewButton extends MenuDrawer {
  constructor() {
    super();

    this.addEventListener('click', () => {
      const wrapper = this.closest('.card-wrapper');
      const drawer = wrapper.querySelector('quick-view-drawer');
      if (drawer) {
        drawer.querySelector('summary').click();

        document.dispatchEvent(new CustomEvent('quickview:open', {
          detail: {
            productUrl: this.dataset.productUrl
          }
        }));
      }
      else if (this.dataset.productUrl) {
        window.location.href = this.dataset.productUrl;
      }
    });
  }
}
customElements.define('quick-view-button', QuickViewButton);

class QuickView extends HTMLElement {
  constructor() {
    super();
    this._html = null;
    this._fetchPromise = null;
  }

  connectedCallback() {
    // Observe the card-wrapper (always visible) so fetch starts before user clicks,
    // not the quick-view element itself which is hidden inside closed <details>.
    const card = this.closest('.card-wrapper') || this;

    new IntersectionObserver((entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      this._prefetch();
    }).observe(card);

    // Also prefetch on hover so content is ready before the click lands
    card.addEventListener('mouseenter', () => this._prefetch(), { once: true });

    // Inject content when this product's popup opens
    document.addEventListener('quickview:open', (e) => {
      if (this._matchesUrl(e.detail?.productUrl)) {
        this._injectWhenReady();
      }
    });
  }

  _matchesUrl(url) {
    if (!url) return false;
    return url.split('?')[0] === this.dataset.productUrl?.split('?')[0];
  }

  _prefetch() {
    if (this._fetchPromise) return;
    const productUrl = this.dataset.productUrl.split('?')[0];
    const sectionUrl = `${productUrl}?view=modal`;

    this._fetchPromise = fetch(sectionUrl)
      .then(response => response.text())
      .then(responseText => {
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const productElement = responseHTML.querySelector('.quick-view__content');
        if (productElement) this._html = productElement.innerHTML;
      })
      .catch(e => console.error(e));
  }

  _injectWhenReady() {
    const drawerContent = this.querySelector('.quick-view__content');
    if (!drawerContent) return;

    const inject = () => {
      if (!this._html) return;

      // Wait for the cover to sweep in before injecting (keeps content hidden during animation)
      setTimeout(() => {
        this.setInnerHTML(drawerContent, this._html);

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        drawerContent.dispatchEvent(new CustomEvent('wishlist:reinit', { bubbles: true, detail: { container: drawerContent } }));
        this._syncWishlistState(drawerContent);

        setTimeout(() => {
          drawerContent.classList.add('hide-cover');

          document.dispatchEvent(new CustomEvent('quickview:loaded', {
            detail: {
              productUrl: this.dataset.productUrl
            }
          }));
        }, 300);
      }, 200);
    };

    if (this._html !== null) {
      inject(); // Already prefetched — instant
    } else {
      if (!this._fetchPromise) this._prefetch();
      this._fetchPromise.then(inject); // Wait for in-flight fetch
    }
  }

  _syncWishlistState(drawerContent) {
    const modalBtn = drawerContent.querySelector('.iWishAdd.st-1[data-product]');
    if (!modalBtn) return;

    const productId = modalBtn.dataset.product;
    const cardBtns = [...document.querySelectorAll(`.iWishAddColl[data-product="${productId}"]`)];
    const allObservers = [];

    const syncCards = () => {
      const isAdded = modalBtn.classList.contains('iwishAdded');
      cardBtns.forEach(cardBtn => cardBtn.classList.toggle('iwishAdded', isAdded));
    };

    const syncModal = () => {
      const isAdded = cardBtns.some(b => b.classList.contains('iwishAdded'));
      modalBtn.classList.toggle('iwishAdded', isAdded);
    };

    const modalObserver = new MutationObserver(syncCards);
    modalObserver.observe(modalBtn, { attributes: true, attributeFilter: ['class'] });
    allObservers.push(modalObserver);

    cardBtns.forEach(cardBtn => {
      const cardObserver = new MutationObserver(syncModal);
      cardObserver.observe(cardBtn, { attributes: true, attributeFilter: ['class'] });
      allObservers.push(cardObserver);
    });

    document.addEventListener('quickview:close', () => allObservers.forEach(o => o.disconnect()), { once: true });
  }

  setInnerHTML(element, html) {
    element.innerHTML = html;

    // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    element.querySelectorAll('script').forEach(oldScriptTag => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach(attribute => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}
customElements.define('quick-view', QuickView);
